const isLocalStorageAvailable = () => {
  try {
    const testKey = '__test_if_localstorage_is_available__';
    // eslint-disable-next-line no-restricted-globals
    localStorage.setItem(testKey, testKey);
    // eslint-disable-next-line no-restricted-globals
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

export class MemoryStorage implements Storage {
  #_data: Record<string, string> = {};

  constructor() {
    // Add proxy to override `ownKeys` and `getOwnPropertyDescriptor` to make
    // MemoryStorage work with `Object.keys` the same way that window.localStorage does.
    // eslint-disable-next-line no-constructor-return
    return new Proxy(this, {
      ownKeys: (target) => Reflect.ownKeys(target.#_data),
      getOwnPropertyDescriptor: (target, prop: string) =>
        Reflect.getOwnPropertyDescriptor(target.#_data, prop),
      get(target, prop) {
        const value = Reflect.get(target, prop);
        if (typeof value === 'function') {
          return value.bind(target);
        }
        return value;
      },
    });
  }

  get length(): number {
    return Object.entries(this.#_data).length;
  }

  key(index: number): string | null {
    return Object.keys(this.#_data)[index] ?? null;
  }

  getItem(key: string): string | null {
    return Object.prototype.hasOwnProperty.call(this.#_data, key)
      ? (this.#_data[key] ?? null)
      : null;
  }

  setItem(key: string, value: string): void {
    this.#_data[key] = String(value);
  }

  removeItem(key: string): void {
    delete this.#_data[key];
  }

  clear(): void {
    this.#_data = {};
  }
}

export const localStorageWithMemoryFallback = isLocalStorageAvailable()
  ? // eslint-disable-next-line no-restricted-properties
    window.localStorage
  : new MemoryStorage();
